import React, { useState, useEffect, useRef, useContext } from 'react';
import DataTable from './DataTable';
import { Search, X, Filter, ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FilterSortContext } from '../lib/FilterSortProvider';
import { useUserData } from '../contexts/UserDataContext';
import PartsPopup from './PartsPopup';
import * as utils from '../lib/utils';

function Dashboard({ tableData, isLoading = false }) {
  const data = tableData || [];
  const navigate = useNavigate();
  const {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    filterLogic,
    setFilterLogic,
    sortConfigs,     
    setSortConfigs,  
    resetAll,        
  } = useContext(FilterSortContext);
  
  // Access user data from context
  const { userData, isLoading: userDataLoading, refreshUserData } = useUserData();

  const [isFocused, setIsFocused] = useState(false);
  const [isSearching, setIsSearching] = useState(Boolean(searchQuery));
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [showPartsPopup, setShowPartsPopup] = useState(false);
  const [selectedParts, setSelectedParts] = useState([]);
  const [selectedPoNumber, setSelectedPoNumber] = useState('');
  const [selectedShipment, setSelectedShipment] = useState('');
  const partsAnchorRef = useRef(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [localData, setLocalData] = useState(data);
  // Add state for active report type
  const [activeReport, setActiveReport] = useState('default');

  // Columns treated as dates
  const dateColumns = ['est_delivery', 'atd', 'ata', 'dest_eta', 'created'];

 
  const hasRequestedRefresh = useRef(false);


  const canCreateShipment = () => {
 
    if (!userData && !userDataLoading) {
      
      if (!hasRequestedRefresh.current) {
        hasRequestedRefresh.current = true;
        refreshUserData();
      }
    }
    
    if (!userData) {
      return false;
    }
    
    
    if (userData.potentiallyStale) {
    }
    
    const result = userData.role !== "Employee";
    return result;
  };

  // Define default report columns
  const defaultColumns = [
    { key: 'forwarder_name', label: 'Forwarder Name' },
    {
      key: 'transit_type',
      label: 'Transit Type',
      render: (row) => {
        const type = row.transit_type;
        const badgeClasses =
          'px-2.5 py-0.5 rounded-full text-xs font-medium inline-block';
        switch (type) {
          case 'SEA':
            return (
              <span className={`${badgeClasses} bg-blue-100 text-blue-800 border border-blue-200`}>
                {type}
              </span>
            );
          case 'AIR':
            return (
              <span className={`${badgeClasses} bg-purple-100 text-purple-800 border border-purple-200`}>
                {type}
              </span>
            );
          case 'COU':
            return (
              <span className={`${badgeClasses} bg-amber-100 text-amber-800 border border-amber-200`}>
                {type}
              </span>
            );
          case 'ROA':
            return (
              <span className={`${badgeClasses} bg-green-100 text-green-800 border border-green-200`}>
                {type}
              </span>
            );
          default:
            if (!type) return null;
            return (
              <span className={`${badgeClasses} bg-gray-100 text-gray-800 border border-gray-200`}>
                {type}
              </span>
            );
        }
      },
    },
    { key: 'title', label: 'PO #' },
    { key: 'cosignee_name', label: 'Consignee Name' },
    { key: 'consignor_name', label: 'Consignor Name' },
    { key: 'origin_port', label: 'Origin Port Code' },
    { key: 'destination_port', label: 'Destination Port' },

 
    {
      key: 'part_numbers',
      label: 'Parts',
      render: (row) => {
        const { part_numbers } = row;
        // If the row has no parts, show a clickable dash with blue styling
        if (!part_numbers || part_numbers.length === 0) {
          return (
            <div
              className="cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
              onClick={(e) => handlePartsClick(row, e)}
            >
              &mdash;
            </div>
          );
        }
       
        const partsList = part_numbers
          .filter(part => part !== null && part !== undefined)
          .map((part) => (typeof part === 'object' ? part.part_number : part))
          .join(', ');
        return (
          <div
            className="max-w-[200px] truncate relative cursor-pointer text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => handlePartsClick(row, e)}
          >
            <span>{partsList}</span>
          </div>
        );
      },
    },

    {
      key: 'total_qty',
      label: 'Total QTY',
      render: (row) => {
        // Check if part_numbers exists and is an array
        if (row.part_numbers && Array.isArray(row.part_numbers) && row.part_numbers.length > 0) {
          // Calculate the sum of all qty values from the part_numbers array
          const totalQty = row.part_numbers.reduce((sum, part) => {
            // Handle both cases: part is an object with qty property or part has a direct qty value
            const qty = typeof part === 'object' && part !== null ? 
              (parseInt(part.qty) || 0) : 0;
            return sum + qty;
          }, 0);
          
          // Format with thousands separator
          return totalQty.toLocaleString();
        }
        
        // If part_numbers doesn't exist or is empty, return dash or 0
        return '—';
      }
    },

    { key: 'est_delivery', label: 'EST Delivery' },
    { key: 'atd', label: 'ATD' },
    { key: 'dest_eta', label: 'Dest. ETA' },
    { key: 'ata', label: 'ATA' },
    { key: 'house', label: 'House' },
    { key: 'master', label: 'Master' },
    { key: 'related_shipment', label: 'Shipment' },
    { key: 'outer', label: 'Outer' },
    {
      key: 'volume',
      label: 'Volume',
      render: (row) => row.volume || '—',
    }, 
    {
      key: 'weight',
      label: 'Weight',
      render: (row) => row.weight || '—',
    },
    { key: 'created', label: 'Created' },
  ];

  // Define DR Report columns in the exact order specified
  const drReportColumns = [
    { key: 'title', label: 'PO #' },
    { key: 'forwarder_name', label: 'Forwarder' },
    { key: 'transit_type', label: 'Transit Type', 
      render: defaultColumns.find(col => col.key === 'transit_type').render 
    },
    {
      key: 'part_numbers',
      label: 'All Parts Numbers',
      render: defaultColumns.find(col => col.key === 'part_numbers').render
    },
    {
      key: 'total_qty',
      label: 'Total QTY',
      render: defaultColumns.find(col => col.key === 'total_qty').render
    },
    { key: 'est_delivery', label: 'Est Delivery' },
    { key: 'atd', label: 'ATD' },
    { key: 'dest_eta', label: 'Dest ETA' },
    { key: 'ata', label: 'ATA' },
    { key: 'house', label: 'House' },
    { key: 'master', label: 'Master' },
    { key: 'related_shipment', label: 'Shipment' },
  ];

  // Get active columns based on the selected report
  const columns = activeReport === 'default' ? defaultColumns : drReportColumns;


  useEffect(() => {
    // Set default sort if no sorts exist
    if (!sortConfigs.length) {
      setSortConfigs([{
        key: 'dest_eta',
        direction: 'descending',
        order: 1
      }]);
    }
  }, []); 

  // Lifecycle
  useEffect(() => {
    setLocalData(data);
  }, [data]);

  useEffect(() => {
    setIsSearching(Boolean(searchQuery));
  }, [searchQuery]);

  // When user clicks on the "Parts" cell
  const handlePartsClick = (row, e) => {
    e.stopPropagation();
    // Even if no parts, we open the popup to add them
    const { part_numbers, related_shipment, title } = row;
    setSelectedRow(row);
    const formattedParts = (part_numbers || []).map((part) => ({
      ...part,
      qty: part.qty ? parseInt(part.qty, 10) : null,
    }));
    setSelectedParts(formattedParts);
    setSelectedShipment(related_shipment);
    setSelectedPoNumber(title || '');
    partsAnchorRef.current = e.currentTarget;
    setShowPartsPopup(true);
  };

  const closePartsPopup = () => {
    setShowPartsPopup(false);
  };

  const handleAddParts = (updatedParts) => {
    setSelectedParts((prev) => [...prev, updatedParts]);
  };

  // Search handlers
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Filter handlers
  const addFilter = () => {
    setFilters((prev) => [
      ...prev,
      { column: columns[0].key, operator: 'contains', value: '' },
    ]);
  };
  const removeFilter = (index) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };
  const updateFilter = (index, field, value) => {
    setFilters((prev) => {
      const newFilters = [...prev];
      newFilters[index] = { ...newFilters[index], [field]: value };
      return newFilters;
    });
  };
  const toggleFilterLogic = () => {
    setFilterLogic((prev) => (prev === 'AND' ? 'OR' : 'AND'));
  };
  const clearFilters = () => {
    setFilters([]);
  };

  // Navigate to a row's detail view by stable identity (PO + shipment).
  // We pass identityList for the form's chevron next/prev navigation —
  // identifiers only, not row data, so it never goes stale.
  const handleRowClick = (rowData, rowIndex, subsetData) => {
    const identityList = (subsetData || []).map(r => ({
      po: r.title,
      shipment: r.related_shipment,
    }));
    const params = new URLSearchParams({
      po: rowData.title,
      shipment: rowData.related_shipment,
    });
    navigate(`/line-item?${params}`, {
      state: { identityList, identityIndex: rowIndex },
    });
  };

  // Listen for clearSearch events to reset search externally
  useEffect(() => {
    const handleClearSearch = () => setSearchQuery('');
    window.addEventListener('clearSearch', handleClearSearch);
    return () => {
      window.removeEventListener('clearSearch', handleClearSearch);
    };
  }, [setSearchQuery]);

  // Filter and Sort logic for CSV Export
  const applyFilters = (dataToFilter) => {
    let filteredData = [...dataToFilter];

    // Global text search
    if (searchQuery) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      filteredData = filteredData.filter((row) => {
        return Object.keys(row).some((key) => {
          const value = row[key];
          if (value === null || value === undefined) return false;
          if (Array.isArray(value)) {
            return value.some((item) =>
              String(item).toLowerCase().includes(lowerCaseQuery)
            );
          }
          return String(value).toLowerCase().includes(lowerCaseQuery);
        });
      });
    }

    // Col filters
    if (filters.length > 0) {
      filteredData = filteredData.filter((row) => {
        const filterResults = filters.map((filter) => {
          const { column, operator, value } = filter;
          const cellValue = row[column];

  
          if (operator === 'isEmpty') {
            return !cellValue || cellValue === '';
          }
          if (operator === 'isNotEmpty') {
            return cellValue && cellValue !== '';
          }
          if (!cellValue) return false;

          // If its a date column
          if (dateColumns.includes(column)) {
            const rowDate = new Date(cellValue);
            const filterDate = new Date(value);
            if (isNaN(rowDate.getTime()) || isNaN(filterDate.getTime())) {
              return false;
            }
            switch (operator) {
              case 'contains':
                return rowDate
                  .toISOString()
                  .split('T')[0]
                  .includes(value);
              case 'equals':
                return (
                  rowDate.toISOString().split('T')[0] ===
                  filterDate.toISOString().split('T')[0]
                );
              case 'is not':
                return (
                  rowDate.toISOString().split('T')[0] !==
                  filterDate.toISOString().split('T')[0]
                );
              case 'startsWith':
                return rowDate
                  .toISOString()
                  .split('T')[0]
                  .startsWith(value);
              case 'endsWith':
                return rowDate
                  .toISOString()
                  .split('T')[0]
                  .endsWith(value);
              case 'greaterThan':
                return rowDate > filterDate;
              case 'lessThan':
                return rowDate < filterDate;
              default:
                return true;
            }
          }

          // Non date col
          const cellStr = String(cellValue).toLowerCase();
          const filterVal = String(value).toLowerCase();
          switch (operator) {
            case 'contains':
              return cellStr.includes(filterVal);
            case 'equals':
              return cellStr === filterVal;
            case 'is not':
              return cellStr !== filterVal;
            case 'startsWith':
              return cellStr.startsWith(filterVal);
            case 'endsWith':
              return cellStr.endsWith(filterVal);
            case 'greaterThan':
              return parseFloat(cellStr) > parseFloat(filterVal);
            case 'lessThan':
              return parseFloat(cellStr) < parseFloat(filterVal);
            default:
              return true;
          }
        });

        return filterLogic === 'AND'
          ? filterResults.every(Boolean)
          : filterResults.some(Boolean);
      });
    }

    return filteredData;
  };

  const applySort = (rows) => {
    if (!sortConfigs || sortConfigs.length === 0) return rows;
    const orderedConfigs = [...sortConfigs].sort((a, b) => a.order - b.order);

    return [...rows].sort((a, b) => {
      for (const config of orderedConfigs) {
        const { key, direction } = config;
        const aVal = a[key];
        const bVal = b[key];

        // Handle null/undefined/empty values (matches DataTable.js behavior)
        if (aVal == null || aVal === '') {
          if (bVal == null || bVal === '') continue;
          return direction === 'ascending' ? -1 : 1;
        }
        if (bVal == null || bVal === '') {
          return direction === 'ascending' ? 1 : -1;
        }
        if (aVal === bVal) continue;

        // Date?
        if (dateColumns.includes(key)) {
          const aDate = new Date(aVal);
          const bDate = new Date(bVal);
          if (direction === 'ascending') {
            return aDate - bDate;
          } else {
            return bDate - aDate;
          }
        }

        // Numeric or string
        const aStr = String(aVal);
        const bStr = String(bVal);
        const aNum = parseFloat(aStr);
        const bNum = parseFloat(bStr);

        if (!isNaN(aNum) && !isNaN(bNum)) {
          return direction === 'ascending' ? aNum - bNum : bNum - aNum;
        } else {
          return direction === 'ascending'
            ? aStr.localeCompare(bStr)
            : bStr.localeCompare(aStr);
        }
      }
      return 0;
    });
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Filter
    const filteredData = applyFilters(data);
    // Sort
    const sortedData = applySort(filteredData);

    if (sortedData.length === 0) {
      alert('No filtered data to export');
      return;
    }

    // Create CSV headers
    const headers = columns
      .filter((col) => col.key !== 'actions')
      .map((col) => `"${col.label}"`);
    const csvRows = [headers.join(',')];

    // Build CSV rows
    sortedData.forEach((row) => {
      const values = columns
        .filter((col) => col.key !== 'actions')
        .map((col) => {
          let value;
          if (col.key === 'part_numbers' && row.part_numbers) {
            // Convert parts array to comma-separated
            value = row.part_numbers
              .map((part) =>
                typeof part === 'object' ? part.part_number : part
              )
              .join(', ');
          } else {
            value = row[col.key] || '';
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        });
      csvRows.push(values.join(','));
    });

    // Download CSV
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `shipment-data-${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Button: new shipment
  const handleNewShipment = () => {
    navigate('/create-shipment');
  };

  // When saving new or updated parts in the popup
  const handleSavePartsChanges = async (updatedParts) => {
    const updatedData = localData.map((row) => {
      if (row === selectedRow) {
        return {
          ...row,
          part_numbers: [...updatedParts],
        };
      }
      return row;
    });
    setLocalData([...updatedData]);
    
    await utils.createParts(updatedParts);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Line Items Dashboard</h1>
        <p className="text-gray-600">Track and manage your line items</p>
      </div>

      {/* Search Bar */}
      <div className="mb-4 relative max-w-md ml-0">
        <div
          className={`
            relative rounded-md overflow-hidden transition-all duration-300 ease-in-out
            ${isFocused ? 'shadow-lg scale-105' : 'shadow'}
            ${isSearching ? 'ring-2 ring-accent-500' : ''}
          `}
        >
          <div
            className={`
              absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none
              transition-all duration-300
              ${isFocused ? 'text-accent-500 scale-110' : 'text-gray-400'}
            `}
          >
            <Search size={18} className={`${isFocused && 'animate-pulse'}`} />
          </div>
          <input
            type="text"
            placeholder="Search across all columns..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`
              w-full pl-12 pr-12 py-2 bg-white border-2 rounded-md
              focus:outline-none transition-all duration-300 ease-in-out
              ${
                isFocused
                  ? 'border-accent-500 bg-white'
                  : 'border-gray-200 hover:border-gray-300'
              }
            `}
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className={`
                absolute inset-y-0 right-0 pr-4 flex items-center
                text-gray-400 hover:text-gray-600 transition-colors duration-200
              `}
            >
              <X
                size={18}
                className="hover:rotate-90 transition-transform duration-300"
              />
            </button>
          )}
        </div>
        {isFocused && (
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-1/3 h-0.5 bg-accent-500 animate-pulse rounded-full"></div>
        )}
      </div>

      {/* Filters & Action Buttons */}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex space-x-2">
          <div 
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium cursor-pointer transition-colors ${
              activeReport === 'default' 
                ? 'bg-accent-100 text-accent-700 border border-accent-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveReport('default')}
          >
            Default Report
          </div>
          <div 
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium cursor-pointer transition-colors ${
              activeReport === 'dr' 
                ? 'bg-accent-100 text-accent-700 border border-accent-200' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
            onClick={() => setActiveReport('dr')}
          >
            DR Report
          </div>
          {searchQuery && (
            <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium animate-pulse">
              <span className="mr-1">Search:</span>
              <span className="font-medium">{searchQuery}</span>
            </div>
          )}
          {filters.length > 0 && (
            <div className="inline-flex items-center px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
              <span className="mr-1">Filters:</span>
              <span className="font-medium">{filters.length} active</span>
            </div>
          )}
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Export
          </button>
          <div className="relative">
            <button
              onClick={() => setFilterOpen((prev) => !prev)}
              className={`px-4 py-2 bg-white border rounded-md text-sm font-medium flex items-center space-x-1 transition-colors ${
                filters.length > 0
                  ? 'border-green-500 text-green-700'
                  : 'border-gray-300 text-gray-700'
              } hover:bg-gray-50`}
            >
              <Filter
                size={16}
                className={filters.length > 0 ? 'text-green-500' : ''}
              />
              <span>Filter</span>
              <ChevronDown
                size={16}
                className={`transition-transform duration-200 ${
                  filterOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {filterOpen && (
              <div className="absolute right-0 mt-2 w-[600px] bg-white rounded-md shadow-lg z-50 border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-gray-800">Filter Data</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={toggleFilterLogic}
                        className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        Logic: {filterLogic}
                      </button>
                      <button
                        onClick={clearFilters}
                        className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
                        disabled={filters.length === 0}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  {filters.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">
                      <p>No filters applied</p>
                      <p className="text-sm">
                        Add a filter to narrow down results
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {filters.map((filter, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2 bg-gray-50 p-2 rounded-md"
                        >
                          <select
                            value={filter.column}
                            onChange={(e) =>
                              updateFilter(index, 'column', e.target.value)
                            }
                            className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                          >
                            {columns.map((col) => (
                              <option key={col.key} value={col.key}>
                                {col.label}
                              </option>
                            ))}
                          </select>
                          <select
                            value={filter.operator}
                            onChange={(e) =>
                              updateFilter(index, 'operator', e.target.value)
                            }
                            className="p-2 border border-gray-300 rounded-md text-sm"
                          >
                            <option value="contains">Contains</option>
                            <option value="equals">Equals</option>
                            <option value="is not">Is Not</option>
                            <option value="startsWith">Starts With</option>
                            <option value="endsWith">Ends With</option>
                            <option value="greaterThan">Greater Than</option>
                            <option value="lessThan">Less Than</option>
                            <option value="isEmpty">Is Empty</option>
                            <option value="isNotEmpty">Is Not Empty</option>
                          </select>
                          {filter.operator !== 'isEmpty' &&
                          filter.operator !== 'isNotEmpty' ? (
                            dateColumns.includes(filter.column) ? (
                              <input
                                type="date"
                                value={filter.value}
                                onChange={(e) =>
                                  updateFilter(index, 'value', e.target.value)
                                }
                                className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                              />
                            ) : (
                              <input
                                type="text"
                                value={filter.value}
                                onChange={(e) =>
                                  updateFilter(index, 'value', e.target.value)
                                }
                                placeholder="Value"
                                className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                              />
                            )
                          ) : (
                            <div className="flex-1 p-2 text-sm text-gray-500 bg-gray-100 rounded-md border border-gray-200">
                              No value needed
                            </div>
                          )}
                          <button
                            onClick={() => removeFilter(index)}
                            className="p-1 text-gray-500 hover:text-red-500 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-4 flex justify-between">
                    <button
                      onClick={addFilter}
                      className="flex items-center space-x-1 text-sm text-accent-600 hover:text-accent-800 transition-colors"
                    >
                      <Plus size={16} />
                      <span>Add Filter</span>
                    </button>
                    <button
                      onClick={() => setFilterOpen(false)}
                      className="px-4 py-2 bg-accent-600 text-white rounded-md text-sm hover:bg-accent-700 transition-colors"
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {canCreateShipment() && (
            <button
              onClick={handleNewShipment}
              className="px-4 py-2 bg-accent-600 rounded-md text-sm font-medium text-white hover:bg-accent-700 flex items-center"
            >
              <Plus size={16} className="mr-2" />
              New Shipment
            </button>
          )}
        </div>
      </div>

      {/* Loading or Data Table */}
      {isLoading ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <div className="flex flex-col items-center justify-center">
            <div className="w-16 h-16 border-t-4 border-accent-600 border-solid rounded-full animate-spin mb-4"></div>
            <h3 className="text-lg font-medium text-gray-800 mb-1">
              Loading Data
            </h3>
            <p className="text-gray-500">
              Please wait while we fetch the latest line items...
            </p>
          </div>
        </div>
      ) : (
        <DataTable
          data={localData}
          columns={columns}
          onRowClick={handleRowClick}
        />
      )}

      {/* Parts Popup */}
      {showPartsPopup && (
        <PartsPopup
          parts={selectedParts}
          isOpen={showPartsPopup}
          onClose={() => {
            setShowPartsPopup(false);
            setSelectedRow(null);
          }}
          poNumber={selectedPoNumber}
          shipment={selectedShipment}
          isFromDashboard={true}
          onSaveChanges={handleSavePartsChanges}
        />
      )}
    </div>
  );
}

export default Dashboard;
